/// <reference types="google-maps" />

import React, { cloneElement, useEffect, useRef, useState } from 'react';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { googleMapConfig } from 'Douleutaras/configuration/googleMap';

import './GoogleAutocompleteField.scss';

export interface CityBounds {
  SOUTH: number;
  WEST: number;
  NORTH: number;
  EAST: number;
}

interface GoogleAutocompleteFieldProps {
  input: React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
    >;
  bounds?: CityBounds; // Restrict autocomplete to certain area
  children?: React.ReactElement;
  handleKeyDown?(event): void;
  onChange(callback): void;
}

export const GoogleAutocompleteField: React.FC<GoogleAutocompleteFieldProps> = ({
                                                                                  bounds,
                                                                                  input,
                                                                                  children,
                                                                                  onChange = callback => true,
                                                                                  handleKeyDown = () => true
}) => {
  const { isLoaded } = useJsApiLoader(googleMapConfig);
  const { countryCode } = useLocalization();
  // tslint:disable:no-null-keyword
  const ref = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete>(null);
  // tslint:enable:no-null-keyword

  const [displayValue, setDisplayValue] = useState(input.value);

  const onLoadAutocomplete = autocomplete => {
    autocompleteRef.current = autocomplete;
  };

  const handlePlaceChanged = () => {
    if (!autocompleteRef || !autocompleteRef.current) {
      return;
    }
    const place = autocompleteRef.current.getPlace();
    onChange(new Gis(place));
  };

  useEffect(() => {
    if (input.value) {
      const v = typeof input.value === 'object'
        // @ts-ignore
        ? input.value.description
        : input.value;

      setDisplayValue(v);

      if (ref.current) {
        ref.current.value = v;
      }
    }
  }, [input.value]);

  if (!isLoaded && children !== undefined) {
    return cloneElement(children, { input: { ...input, defaultValue: displayValue, disabled: 'disabled' }});
  } else if (!isLoaded && typeof children === 'undefined') {
    return <input className={input.className}
                  placeholder={input.placeholder}
                  disabled
    />;
  }

  const acOptions = {
    types: ['geocode'],
    componentRestrictions: { country: countryCode },
    bounds: undefined
  };

  if (bounds) {
    acOptions.bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(bounds.SOUTH, bounds.WEST),
      new google.maps.LatLng(bounds.NORTH, bounds.EAST)
    );
  }

  return (
    <Autocomplete
      options={acOptions}
      onLoad={onLoadAutocomplete}
      onPlaceChanged={handlePlaceChanged}
    >
      { children !== undefined
        ? cloneElement(children, { input: { ...input, defaultValue: displayValue }})
        : <input ref={ref}
                 className={input.className}
                 placeholder={input.placeholder}
                 defaultValue={displayValue}
        />
      }
    </Autocomplete>
  );
};
